import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon, Plus, Trash2, GitCompare, Loader2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { SERIES_COLORS, colorFor } from "./chartConstants";

/**
 * Panneau de comparaison multi-périodes.
 * Permet d'ajouter N intervalles arbitraires, chacun avec sa couleur.
 */
export default function MultiYearCompare({ orderTypeIds, sellerIds }) {
  const now = new Date();
  const yearStart = (y) => new Date(y, 0, 1);
  const yearEnd = (y) => new Date(y, 11, 31);

  const [ranges, setRanges] = useState([
    { label: `${now.getFullYear()}`, start: yearStart(now.getFullYear()), end: now, color: SERIES_COLORS[0] },
    { label: `${now.getFullYear() - 1}`, start: yearStart(now.getFullYear() - 1), end: yearEnd(now.getFullYear() - 1), color: SERIES_COLORS[1] },
  ]);
  const [metric, setMetric] = useState("ca");
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  const addRange = () => {
    const idx = ranges.length;
    const y = now.getFullYear() - idx - 1;
    setRanges([...ranges, {
      label: `${y}`,
      start: yearStart(y),
      end: yearEnd(y),
      color: colorFor(idx),
    }]);
  };

  const removeRange = (i) => setRanges(ranges.filter((_, idx) => idx !== i));

  const updateRange = (i, patch) => {
    setRanges(ranges.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const runCompare = async () => {
    setLoading(true);
    try {
      const payload = ranges.map((r) => ({
        label: r.label,
        start_date: r.start.toISOString(),
        end_date: r.end.toISOString(),
        color: r.color,
      }));
      const params = {};
      if (orderTypeIds?.length) params.order_type_ids = orderTypeIds.join(",");
      if (sellerIds?.length) params.seller_ids = sellerIds.join(",");
      const res = await api.getAnalyticsMultiYear(payload, { ...params, metric });
      setSeries(res.data.series || []);
    } catch (e) {
      toast.error("Erreur lors de la comparaison");
    } finally {
      setLoading(false);
    }
  };

  // Construit un dataset croisé : pour chaque index de mois, valeurs par série
  const chartData = (() => {
    const maxLen = Math.max(...series.map((s) => s.points.length), 0);
    const out = [];
    for (let i = 0; i < maxLen; i++) {
      const row = { index: i, label: series[0]?.points[i]?.period_label?.split(" ")[0] || `M+${i + 1}` };
      series.forEach((s) => {
        const p = s.points[i];
        if (p) {
          row[s.label] = p.value;
          row[`${s.label}__count`] = p.count;
          row[`${s.label}__period`] = p.period_label;
        }
      });
      out.push(row);
    }
    return out;
  })();

  const CompareTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="bg-white p-3 border rounded shadow-lg text-xs space-y-1">
        <p className="font-semibold mb-1">{label}</p>
        {payload.map((entry, i) => {
          const periodLabel = chartData[entry.payload.index]?.[`${entry.dataKey}__period`];
          const count = chartData[entry.payload.index]?.[`${entry.dataKey}__count`];
          return (
            <p key={i} style={{ color: entry.color }}>
              <span className="font-medium">{entry.dataKey}</span> ({periodLabel}) :{" "}
              {metric === "ca" ? formatCurrency(entry.value) : formatCurrency(entry.value)}
              {count > 0 && <span className="text-muted-foreground"> · {count} chantier{count > 1 ? "s" : ""}</span>}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-primary" />
            Comparaison multi-périodes
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={metric} onValueChange={setMetric}>
              <SelectTrigger className="h-9 w-44 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ca">Chiffre d'affaires</SelectItem>
                <SelectItem value="orders">Commandes</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={addRange} data-testid="add-compare-range">
              <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
            </Button>
            <Button size="sm" onClick={runCompare} disabled={loading || ranges.length === 0} data-testid="run-multi-year">
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null} Lancer
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          {ranges.map((r, i) => (
            <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`compare-range-${i}`}>
              <div className="w-3 h-3 rounded-full" style={{ background: r.color }} />
              <Input
                value={r.label}
                onChange={(e) => updateRange(i, { label: e.target.value })}
                className="w-32 h-8 text-xs"
                placeholder="Nom"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs">
                    <CalendarIcon className="h-3 w-3 mr-1" />
                    {format(r.start, "dd/MM/yy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={r.start} onSelect={(d) => d && updateRange(i, { start: d })} locale={fr} />
                </PopoverContent>
              </Popover>
              <span className="text-xs text-muted-foreground">→</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs">
                    <CalendarIcon className="h-3 w-3 mr-1" />
                    {format(r.end, "dd/MM/yy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={r.end} onSelect={(d) => d && updateRange(i, { end: d })} locale={fr} />
                </PopoverContent>
              </Popover>
              <input
                type="color"
                value={r.color}
                onChange={(e) => updateRange(i, { color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer border"
                title="Couleur"
              />
              {ranges.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeRange(i)} className="h-8 w-8 p-0">
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {series.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                label={{ value: "Mois", position: "insideBottom", offset: -5, fontSize: 11 }}
              />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} tick={{ fontSize: 11 }} />
              <Tooltip content={<CompareTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {series.map((s) => (
                <Line
                  key={s.label}
                  type="monotone"
                  dataKey={s.label}
                  stroke={s.color || "#1d4ed8"}
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-muted-foreground text-sm">
            Ajoute des plages puis clique sur « Lancer » pour comparer.
          </div>
        )}

        {series.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
            {series.map((s) => (
              <div key={s.label} className="flex items-center gap-2 text-xs p-2 rounded bg-zinc-50">
                <div className="w-3 h-3 rounded-full" style={{ background: s.color || "#1d4ed8" }} />
                <div className="flex-1">
                  <p className="font-medium">{s.label}</p>
                  <p className="text-muted-foreground">{formatCurrency(s.total)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
